import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Settings, Save, X, Info, Download, Upload, RotateCcw, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from './ui/dialog';
import LoginPromptDialog from './LoginPromptDialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_RULES = `1️⃣ Authority Rule (Non-Negotiable Foundation)
• The Qur'an is the only source of truth. Quran explains itself
• Do not rely on: Hadith, Tafsīr, Historical reports, Classical theology, Traditional grammar assumptions
• If external meaning appears, it must be explicitly confirmed by Qur'anic usage or rejected.

📖 Reference: 6:114, 12:111, 15:9

2️⃣ Nature Coherence Rule
• The Qur'an cannot contradict natural law.
• Meaning must align with: Physics, Biology, Ecology, Psychology, Systems theory
• If interpretation violates observable reality, reassess.

3️⃣ Ethical Priority Rule
• When two meanings are possible, choose the one that promotes: Justice, Awareness, Growth, Responsibility
• The Qur'an is guidance (2:2), so interpretation must elevate.

4️⃣ Preservation Rule
• Allah preserves meaning, not institutions. (15:9, 41:42)
• The Qur'an must self-explain without external scaffolding.

5️⃣ Custom Root Meaning Rule
• Custom root meanings entered in this App should be given more preference than traditional meanings.
6️⃣ Word Analysis Rule (Distributional Method)

Treat every Arabic word as:
	•	One root system
	•	One unified semantic field
	•	Context-determined sub-meanings

Avoid fixed labels like noun/verb/adjective. Meaning emerges from:
	1.	All occurrences in Qur’an
	2.	Opposites and contrasts
	3.	Parallel structures
	4.	Repeated patterns
	5.	Root relationships

AI Process:
	1.	Extract root
	2.	check all occurrences
	3.	Observe context patterns
	4.	Identify opposites
	5.	Form multiple hypotheses
	6.	Narrow based on coherence

CORE METHODOLOGY (Qur'anic Self-Explanation):
1. The Quran explains itself - meanings should be derived from HOW the word is used across different contexts
2. Look for PATTERNS and CONSISTENT themes across all verses
3. Consider the broader context and narrative of each verse
4. Do NOT rely on traditional translations or external tafseers
5. The Arabic language and the Quran's internal logic are your primary sources
6. Consider metaphorical and conceptual meanings, not just literal ones

EXAMPLE METHODOLOGY - Deriving Word meaning from context:
The word "Ghulam" (غلام) traditionally means "young boy". But analyzing Quranic contexts:

• Yusuf (12:19): Water drawer finds a Ghulam
  - Water represents knowledge. Someone who draws water is actively seeking knowledge.
  - Ghulam = an unexpected but valuable NEW INSIGHT/IDEA/APPROACH that needs nurturing

• Zakariyya (19:7-8): He receives news of Ghulam (Yahya)
  - Yahya means "To Live/Gift of Life"
  - Wife's barrenness = ideological stagnation, lack of new perspectives
  - Ghulam = a new idea/approach that will come to life for his mission

• Maryam (19:19-20): A pure Ghulam (Isa)
  - "Untouched by man" = mission not corrupted by man-made ideologies
  - Pure Ghulam = an untainted new insight not corrupted by traditions

• Al-Kahf (18:74): The servant kills a Ghulam
  - Killing a Ghulam = killing a flawed idea/approach before it matures
  - The wise one knew this approach would negatively impact those on the right path

PATTERN: Ghulam represents an unexpected but valuable NEW IDEA/INSIGHT/APPROACH that needs proper nurturing to reach its full potential.

Apply this same analytical approach to derive Word meanings from Arabic Quranic context

EXAMPLE 2: METHODOLOGY - Deriving Word meaning from context:

What does Bismillah really mean?
Most of us think it simply means “in the name of Allah.” But the Qur’an uses two different forms:
 • bi-ismi (with alif) → like in Iqraʾ bi-ismi rabbik (96:1)
 • bism (without alif) → like in Nūḥ’s ark (11:41) and Sulayman’s letter (27:30)
If both were the same, why keep two forms? 
⸻
Clue from the ant story (27:18–19):
When the ant raised concern, Sulayman “tabassama” (from the same root ب-س-م) — a gentle smile that acknowledged the concern and turned it into trusting and friendly tone.
So bsm isn’t just “by name.” It carries the sense of an outward expression to transform someones  concern to a friendly, understanding, trustworthy tone.
⸻
Now apply it:
 • Noah (11:41): “embark there in bismillah” → to address the concerned people, he says to start the journey with him which will transform the concern of the people and will create understanding and trust with Allahs laws.
 • Sulayman’s letter (27:30): Queen Sheeba says “It is from Sulayman, and it is bismillah al-Raḥmān al-Raḥīm” → not just a preface, but the entire letter itself is framed as a gesture which relieves the concern of the Queen sheeba and builds trust and understand with Allahs laws.
⸻
Conclusion:
Bismillah = an open expression by Allah that acknowledges the audience concern and builds trust.
Just like Sulayman’s letter, the entire Qur’an itself is Bismillah al-Raḥmān, al-Raḥīm — a gesture of transforming our concerns to building an understanding and trust with Allah The Rehman and the Raheem.

Other Meanings
The following names of animals have been used in the Quran as a metaphor, the Arabic lexicon-roots also supports the given version.
Tayran-Ababeel. = the smart dominating high-handing people.
Al-anaam[the cattles] = the skilled labour[as a human resource] 
Yellow Cow. = the unquestioned belief [dogmatic relegion]
She Camel [naqah] = The appressed group of people  
Dog = the determined-vigillent 
Donkey= the religious silly clerics[who shouts loudly].
The elephant rider [As-haab e feel] = the ediot pumpos people 
A giant Fish[hoot]. The haughty coning clerics
Adiyat [the Horse].= A person who surpasses the limits
Hud Hud [hoopoe] = the most guided one
Mosquito.= the meager thing
Kurud [the Monkey]. Used for the Monkey-business 
Khinzeer [the Pig]. used for the pig-economy
Ghuraab [the Raven].=the stranger
Hayya [the Snake]. = The apparent alive-evidence
Zeibb [the Wolf].= The merciless contender


`;

const TranslationSettings = ({ open: externalOpen, onOpenChange: setExternalOpen }) => {
    const isControlled = externalOpen !== undefined && setExternalOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);

    const isOpen = isControlled ? externalOpen : internalOpen;
    const setIsOpen = isControlled ? setExternalOpen : setInternalOpen;

    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [rules, setRules] = useState('');
    const [originalRules, setOriginalRules] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Export/Import states
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [importData, setImportData] = useState(null);
    const [mergeMode, setMergeMode] = useState(true);
    const fileInputRef = useRef(null);

    // Gate settings open behind auth
    const handleOpenSettings = () => {
        if (!isAuthenticated) {
            setShowLoginPrompt(true);
            return;
        }
        setIsOpen(true);
    };

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/translation-settings`, {
                headers: getAuthHeaders()
            });
            const loadedRules = response.data.settings?.rules || '';
            setRules(loadedRules || DEFAULT_RULES);
            setOriginalRules(loadedRules || DEFAULT_RULES);
        } catch (err) {
            console.error('Failed to load settings:', err);
            setRules(DEFAULT_RULES);
            setOriginalRules(DEFAULT_RULES);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        if (isOpen) {
            loadSettings();
        }
    }, [isOpen, loadSettings]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/translation-settings`, { rules }, {
                headers: getAuthHeaders()
            });
            setOriginalRules(rules);
            toast.success('Translation rules saved');
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setRules(DEFAULT_RULES);
    };

    // Export all user data
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const response = await axios.get(`${API}/export-data`, {
                headers: getAuthHeaders()
            });
            const data = response.data;

            // Create and download JSON file
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `quran-lexicon-backup-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            const stats = {
                meanings: data.custom_meanings?.length || 0,
                translations: data.custom_translations?.length || 0,
                topics: data.topics?.length || 0
            };

            toast.success(`Exported ${stats.meanings} meanings, ${stats.translations} translations, and ${stats.topics} topics`);
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Failed to export data');
        } finally {
            setIsExporting(false);
        }
    };

    // Handle file selection for import
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = JSON.parse(event.target.result);

                // Validate the data structure
                if (!data.custom_meanings && !data.custom_translations && !data.translation_settings && !data.topics) {
                    toast.error('Invalid backup file format');
                    return;
                }

                setImportData(data);
                setShowImportDialog(true);
            } catch (err) {
                console.error('Failed to parse import file:', err);
                toast.error('Invalid JSON file');
            }
        };
        reader.readAsText(file);

        // Reset file input
        e.target.value = '';
    };

    // Perform the import
    const handleImport = async () => {
        if (!importData) return;

        setIsImporting(true);
        try {
            const response = await axios.post(`${API}/import-data`, {
                data: importData,
                merge: mergeMode
            }, {
                headers: getAuthHeaders()
            });

            const stats = response.data.stats;
            toast.success(
                `Import complete! Added ${stats.meanings_imported} meanings, ${stats.translations_imported} translations` +
                (stats.meanings_skipped > 0 || stats.translations_skipped > 0
                    ? ` (${stats.meanings_skipped + stats.translations_skipped} duplicates skipped)`
                    : '')
            );

            // Reload settings if they were imported
            if (stats.settings_imported) {
                loadSettings();
            }

            setShowImportDialog(false);
            setImportData(null);
        } catch (err) {
            console.error('Import failed:', err);
            toast.error('Failed to import data');
        } finally {
            setIsImporting(false);
        }
    };

    // Reset all data
    const handleResetAll = async () => {
        try {
            const response = await axios.post(`${API}/reset-data`, {}, {
                headers: getAuthHeaders()
            });
            toast.success(response.data.message);
            setShowResetConfirm(false);

            // Reload settings (will get default)
            loadSettings();
        } catch (err) {
            console.error('Reset failed:', err);
            toast.error('Failed to reset data');
        }
    };

    const hasChanges = rules !== originalRules;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                {!isControlled && (
                    <DialogTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#94a3b8] hover:text-[#d4af37] hover:bg-[#1e293b]"
                            data-testid="settings-btn"
                            onClick={(e) => {
                                if (!isAuthenticated) {
                                    e.preventDefault();
                                    setShowLoginPrompt(true);
                                }
                            }}
                        >
                            <Settings className="w-5 h-5" />
                        </Button>
                    </DialogTrigger>
                )}
                <DialogContent
                    className="w-full sm:max-w-2xl bg-[#09090b] border-[#1e293b] p-0 overflow-hidden max-h-[85vh] text-[#f8fafc]"
                    data-testid="translation-settings-panel"
                >
                    <DialogHeader className="p-4 sm:p-6 border-b border-[#1e293b]">
                        <DialogTitle className="text-[#f8fafc] heading-primary text-xl sm:text-2xl flex items-center gap-3">
                            <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37]" />
                            AI Translation Rules
                        </DialogTitle>
                    </DialogHeader>

                    <div className="p-4 sm:p-6 space-y-4 h-[calc(85vh-150px)] min-h-[50vh] overflow-y-auto">
                        {/* Info Banner */}
                        <div className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#1e293b] flex gap-3">
                            <Info className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-[#94a3b8]">
                                <p className="mb-2">
                                    These rules guide the AI when generating custom translations.
                                    The AI will follow these principles along with your custom word meanings.
                                </p>
                                <p className="text-[#d4af37]">
                                    Your custom word meanings always take priority over standard meanings.
                                </p>
                            </div>
                        </div>

                        {/* Rules Editor */}
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] ui-text font-medium">
                                Global Translation Rules & Context
                            </label>
                            <Textarea
                                value={rules}
                                onChange={(e) => setRules(e.target.value)}
                                placeholder="Enter your translation rules and principles..."
                                className="min-h-[300px] bg-[#0f172a]/50 border-[#1e293b] text-[#f8fafc] text-sm leading-relaxed font-mono"
                                disabled={loading}
                                data-testid="rules-textarea"
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t border-[#1e293b]">
                            <Button
                                variant="outline"
                                onClick={handleReset}
                                className="border-[#1e293b] text-[#94a3b8] hover:text-[#f8fafc]"
                                data-testid="reset-rules-btn"
                            >
                                Reset to Default
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!hasChanges || saving}
                                className="bg-[#d4af37] text-[#02040a] hover:bg-[#facc15] font-semibold disabled:opacity-50"
                                data-testid="save-rules-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {saving ? 'Saving...' : 'Save Rules'}
                            </Button>
                        </div>

                        {/* Data Management Section */}
                        <div className="pt-6 border-t border-[#1e293b] space-y-4">
                            <h3 className="text-[#f8fafc] font-medium flex items-center gap-2">
                                <Download className="w-4 h-4 text-[#d4af37]" />
                                Data Management
                            </h3>

                            <p className="text-sm text-[#64748b]">
                                Export your custom meanings, translations, and rules as a backup file.
                                Import to restore or merge data from another device.
                            </p>

                            {/* Hidden file input */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept=".json"
                                className="hidden"
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {/* Export Button */}
                                <Button
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    variant="outline"
                                    className="border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10"
                                    data-testid="export-data-btn"
                                >
                                    {isExporting ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4 mr-2" />
                                    )}
                                    Export Data
                                </Button>

                                {/* Import Button */}
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isImporting}
                                    variant="outline"
                                    className="border-[#3b82f6] text-[#3b82f6] hover:bg-[#3b82f6]/10"
                                    data-testid="import-data-btn"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    Import Data
                                </Button>

                                {/* Reset All Button */}
                                <Button
                                    onClick={() => setShowResetConfirm(true)}
                                    variant="outline"
                                    className="border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444]/10"
                                    data-testid="reset-all-btn"
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Reset All
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>

                {/* Import Dialog */}
                <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                    <DialogContent className="bg-[#09090b] border-[#1e293b] text-[#f8fafc]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Upload className="w-5 h-5 text-[#3b82f6]" />
                                Import Data
                            </DialogTitle>
                            <DialogDescription className="text-[#94a3b8]">
                                Choose how to handle the imported data
                            </DialogDescription>
                        </DialogHeader>

                        {importData && (
                            <div className="space-y-4 py-4">
                                {/* Import preview */}
                                <div className="p-3 bg-[#0f172a]/50 rounded-lg border border-[#1e293b] space-y-2">
                                    <p className="text-sm text-[#94a3b8]">File contains:</p>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="text-xs bg-[#d4af37]/10 text-[#d4af37] px-2 py-1 rounded">
                                            {importData.custom_meanings?.length || 0} meanings
                                        </span>
                                        <span className="text-xs bg-[#10b981]/10 text-[#10b981] px-2 py-1 rounded">
                                            {importData.custom_translations?.length || 0} translations
                                        </span>
                                        {importData.translation_settings?.rules && (
                                            <span className="text-xs bg-[#3b82f6]/10 text-[#3b82f6] px-2 py-1 rounded">
                                                Settings included
                                            </span>
                                        )}
                                        {(importData.topics?.length > 0) && (
                                            <span className="text-xs bg-[#f59e0b]/10 text-[#f59e0b] px-2 py-1 rounded">
                                                {importData.topics.length} topics
                                            </span>
                                        )}
                                    </div>
                                    {importData.export_date && (
                                        <p className="text-xs text-[#64748b]">
                                            Exported: {new Date(importData.export_date).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>

                                {/* Merge option */}
                                <div className="space-y-3">
                                    <p className="text-sm font-medium">Import mode:</p>
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-3 p-3 rounded-lg border border-[#1e293b] cursor-pointer hover:border-[#3b82f6]/50 transition-colors">
                                            <input
                                                type="radio"
                                                name="importMode"
                                                checked={mergeMode}
                                                onChange={() => setMergeMode(true)}
                                                className="accent-[#3b82f6]"
                                            />
                                            <div>
                                                <p className="text-sm font-medium">Merge (recommended)</p>
                                                <p className="text-xs text-[#64748b]">Add new data, skip duplicates</p>
                                            </div>
                                        </label>
                                        <label className="flex items-center gap-3 p-3 rounded-lg border border-[#1e293b] cursor-pointer hover:border-[#ef4444]/50 transition-colors">
                                            <input
                                                type="radio"
                                                name="importMode"
                                                checked={!mergeMode}
                                                onChange={() => setMergeMode(false)}
                                                className="accent-[#ef4444]"
                                            />
                                            <div>
                                                <p className="text-sm font-medium text-[#ef4444]">Replace all</p>
                                                <p className="text-xs text-[#64748b]">Delete existing data first</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <DialogFooter className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowImportDialog(false);
                                    setImportData(null);
                                }}
                                className="border-[#1e293b] text-[#94a3b8]"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleImport}
                                disabled={isImporting}
                                className="bg-[#3b82f6] text-white hover:bg-[#2563eb]"
                            >
                                {isImporting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Importing...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4 mr-2" />
                                        Import
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reset Confirmation Dialog */}
                <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
                    <DialogContent className="bg-[#09090b] border-[#1e293b] text-[#f8fafc]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-[#ef4444]">
                                <AlertTriangle className="w-5 h-5" />
                                Reset All Data
                            </DialogTitle>
                            <DialogDescription className="text-[#94a3b8]">
                                This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="py-4">
                            <p className="text-sm text-[#f8fafc]">
                                This will permanently delete:
                            </p>
                            <ul className="mt-2 space-y-1 text-sm text-[#94a3b8]">
                                <li>• All custom word meanings</li>
                                <li>• All custom verse translations</li>
                                <li>• Your translation rules (reset to default)</li>
                            </ul>
                            <p className="mt-4 text-sm text-[#f59e0b]">
                                Consider exporting your data first as a backup.
                            </p>
                        </div>

                        <DialogFooter className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowResetConfirm(false)}
                                className="border-[#1e293b] text-[#94a3b8]"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleResetAll}
                                className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Reset Everything
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Dialog>

            {/* Login Prompt Dialog */}
            <LoginPromptDialog
                isOpen={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                featureName="Settings"
            />
        </>
    );
};

export default TranslationSettings;
